/**
 * Deployment-relative URLs.
 *
 * The same frontend build runs on app.eurion.se and on every customer deployment
 * (app.<customer-domain>), so nothing here may hard-code eurion.se.
 */

/** Settings the customer kit writes to /runtime/config.js (absent in dev and the desktop shell). */
export interface EurionRuntime {
  /** "single": the whole deployment on one hostname (admin portal at /admin) */
  routing?: 'multi' | 'single';
  adminUrl?: string;
  /** Where the desktop installer and Android APK are downloaded from (a folder URL) */
  downloadsUrl?: string;
}

export function runtimeConfig(): EurionRuntime {
  if (typeof window === 'undefined') return {};
  return (window as unknown as { __EURION_RUNTIME__?: EurionRuntime }).__EURION_RUNTIME__ ?? {};
}

/**
 * Admin panel URL: explicit VITE_ADMIN_URL wins, then the deployment's own setting
 * (runtime config: admin.<domain> or <host>/admin); otherwise app.<domain> → admin.<domain>
 * on the current host (any other host gets an admin. prefix). Falls back to
 * admin.eurion.se outside a browser (e.g. the Tauri desktop shell's custom origin).
 */
export function adminUrl(): string {
  const env = import.meta.env.VITE_ADMIN_URL as string | undefined;
  if (env) return env;
  const runtime = runtimeConfig().adminUrl;
  if (runtime) return runtime;
  if (typeof window === 'undefined' || !/^https?:$/.test(window.location.protocol)) {
    return 'https://admin.eurion.se';
  }
  const { protocol, host, origin } = window.location;
  if (host.startsWith('admin.')) return origin;   // already on the admin host
  if (host.startsWith('localhost') || /^\d+\.\d+\.\d+\.\d+(:\d+)?$/.test(host)) return 'https://admin.eurion.se';
  const adminHost = host.startsWith('app.') ? `admin.${host.slice(4)}` : `admin.${host}`;
  return `${protocol}//${adminHost}`;
}

/**
 * The SCIM 2.0 base URL an identity provider (Entra, Okta …) is given: an absolute VITE_API_URL,
 * else this deployment's app host + /api/v1/scim — also when the panel is shown in the admin
 * portal (admin.<domain> → app.<domain>), so admins always copy the address the docs describe.
 */
export function scimEndpointUrl(): string {
  const env = import.meta.env.VITE_API_URL as string | undefined;
  if (env && /^https?:\/\//.test(env)) return `${env.replace(/\/$/, '').replace(/\/api$/, '')}/api/v1/scim`;
  if (typeof window === 'undefined' || !/^https?:$/.test(window.location.protocol)) {
    return 'https://app.eurion.se/api/v1/scim';
  }
  const { protocol, host, origin } = window.location;
  if (runtimeConfig().routing === 'single') return `${origin}/api/v1/scim`;
  const appHost = host.startsWith('admin.') ? `app.${host.slice('admin.'.length)}` : host;
  return `${protocol}//${appHost}/api/v1/scim`;
}

/** Eurion's public download folder (desktop installer, Android APK), next to the update channels. */
export const PUBLIC_DOWNLOADS_URL = 'https://updates.eurion.se/downloads/';

/**
 * Folder the app downloads are offered from: VITE_DOWNLOADS_URL, then the deployment's
 * runtime config (a customer may mirror the files), else Eurion's public download folder.
 * Always ends with a slash.
 */
export function downloadsUrl(): string {
  const url = (import.meta.env.VITE_DOWNLOADS_URL as string | undefined) || runtimeConfig().downloadsUrl || PUBLIC_DOWNLOADS_URL;
  return url.endsWith('/') ? url : `${url}/`;
}
